import { useResizeObserver } from "@amzn/stencil-react-components/hooks";
import { Col, View } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { SearchResult } from "common/components";
import { useLists } from "common/context/lists/ListsContext";
import { CatalogItemSimplified } from "common/types/catalog-types";
import { CatalogItemSimplifiedWithRecommendations } from "common/types/catalogical-type";
import { PERMISSIONS } from "config";
import { useSearchExperience } from "hooks/useSearchExperience";
import { Visibility } from "lib/models/list-models";
import { UserPermissions } from "lib/services";
import React, { FC, useEffect, useLayoutEffect } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { CatalogItemsListProps } from "../CatalogItemsList";

export * from "./reducer";

const CatalogicalItemsList: FC<CatalogItemsListProps> = (props) => {
  const { items, totalCount, isLoading, searchCatalogItems } = useSearchExperience();
  const toggleLists = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_LISTS);

  const { ref, height } = useResizeObserver<HTMLDivElement>();

  const hasMoreItems = () => {
    return items!.length < totalCount!;
  };

  const { myLists, getMyLists } = useLists();
  useEffect(() => {
    if (toggleLists) {
      getMyLists({
        allowedDomains: ["software"],
      });
    }
  }, [toggleLists]);

  /* istanbul ignore next */
  useLayoutEffect(() => {
    if (!hasMoreItems()) return;
    if (props.shouldLoadMore()) {
      props.onSearchNext();
    }
  }, [height, isLoading]);

  /* istanbul ignore next */
  const itemsComponents = props.items!.map((item) => {
    const itemWithRec = item as CatalogItemSimplifiedWithRecommendations;

    return (
      <SearchResult
        key={`${itemWithRec.itemId}`}
        taxonomyId={itemWithRec.itemId || ""}
        logoUrl={itemWithRec.logoImageUrl || ""}
        name={itemWithRec.name || ""}
        shortDescription={itemWithRec.shortDescription || ""}
        available={itemWithRec.status || ""}
        useSearchExperience
        tags={[itemWithRec.itptClassification ?? ""]}
        price={itemWithRec.price}
        os={itemWithRec.os}
        eta={itemWithRec.etaDays}
        score={itemWithRec.score}
        recommendations={itemWithRec.recommendations}
        lists={myLists}
      />
    );
  });

  return (
    <Col ref={ref}>
      <InfiniteScroll
        dataLength={totalCount}
        next={props.onSearchNext}
        hasMore={hasMoreItems()}
        loader={
          <View padding="S700">
            <Spinner size={SpinnerSize.Medium} />
          </View>
        }
        scrollableTarget={props.scrollableTarget}
      >
        {itemsComponents}
      </InfiniteScroll>
    </Col>
  );
};

export default CatalogicalItemsList;
